import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toggle } from '@/components/ui/toggle';
import { OnboardingShell } from '@/features/onboarding/components/onboarding-shell';
import { trackOnboardingEvent, useTrackOnboardingStartedOnce } from '@/features/onboarding/analytics';
import type { OnboardingBuildMode, OnboardingGoalId } from '@/features/onboarding/data';
import {
    useIsUpdatingOnboardingState,
    useOnboardingState,
    useUpdateOnboardingState
} from '@/features/onboarding/hooks/useOnboardingState';
import { formatOnboardingPrice, getOnboardingPrice } from '@/features/onboarding/pricing';
import { requireOnboardingStep } from '@/features/onboarding/route-guards';
import { useOnboardingStore } from '@/features/onboarding/store';
import { useCurrentOrganizationId } from '@/features/organizations/store/useCurrentOrganizationStore';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';
import { cn } from '@/lib/utils';

const MIN_SOURCES = 2;
const MAX_SOURCES = 100;
const GOALS: OnboardingGoalId[] = ['publish', 'comments', 'analytics', 'ai', 'ugc', 'webhooks'];
const BUILD_MODES: OnboardingBuildMode[] = ['code', 'agent', 'both'];

export const Route = createFileRoute('/onboarding/personalize')({
    beforeLoad: () => requireOnboardingStep(1),
    component: PersonalizeRoute
});

export function PersonalizeRoute() {
    useTrackOnboardingStartedOnce();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { goals, buildMode, sources, toggleGoal, setBuildMode, setSources, setStep } = useOnboardingStore();
    const organizationId = useCurrentOrganizationId();
    const { data: serverState } = useOnboardingState();
    const updateState = useUpdateOnboardingState(organizationId);
    const { mutate: updateStateMutate, mutateAsync: updateStateMutateAsync } = updateState;
    const isUpdatingOnboardingState = useIsUpdatingOnboardingState(organizationId);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isFormDisabled = isSubmitting || isUpdatingOnboardingState;

    const sourcesPatchTimerRef = useRef<number | null>(null);
    const queuedSourcesPatchRef = useRef<number | null>(null);
    const sourcesPatchQueueRef = useRef<Promise<void>>(Promise.resolve());
    const seenAtSentRef = useRef(false);

    useEffect(() => {
        if (
            seenAtSentRef.current ||
            !organizationId ||
            !serverState ||
            serverState.completed ||
            serverState.seenAt !== null
        ) {
            return;
        }

        seenAtSentRef.current = true;
        updateStateMutate({ seenAt: new Date().toISOString() });
    }, [organizationId, serverState, updateStateMutate]);

    useEffect(() => {
        return () => {
            if (sourcesPatchTimerRef.current !== null) {
                window.clearTimeout(sourcesPatchTimerRef.current);
            }
        };
    }, []);

    function queueSourcesPatch(nextSources: number) {
        if (!organizationId || serverState?.completed) {
            return;
        }

        if (sourcesPatchTimerRef.current !== null) {
            window.clearTimeout(sourcesPatchTimerRef.current);
        }

        sourcesPatchTimerRef.current = window.setTimeout(() => {
            sourcesPatchTimerRef.current = null;
            enqueueSourcesPatch(nextSources);
        }, 600);
    }

    function enqueueSourcesPatch(nextSources: number) {
        queuedSourcesPatchRef.current = nextSources;

        const patchTask = sourcesPatchQueueRef.current.then(async () => {
            const sourcesToPatch = queuedSourcesPatchRef.current;
            queuedSourcesPatchRef.current = null;

            if (sourcesToPatch === null || !organizationId || useOnboardingStore.getState().checkout === 'completed') {
                return;
            }

            await updateStateMutateAsync({ sources: sourcesToPatch });
        });

        sourcesPatchQueueRef.current = patchTask.catch(() => undefined);
        void patchTask.catch(() => undefined);
    }

    async function submitPersonalization() {
        if (!organizationId || serverState?.completed || useOnboardingStore.getState().checkout === 'completed') {
            setIsSubmitting(false);
            return;
        }

        if (sourcesPatchTimerRef.current !== null) {
            window.clearTimeout(sourcesPatchTimerRef.current);
            sourcesPatchTimerRef.current = null;
        }

        queuedSourcesPatchRef.current = null;
        await sourcesPatchQueueRef.current;

        updateStateMutate(
            { goals, buildMode, sources, currentStep: 'connect' },
            {
                onSuccess: () => {
                    setStep(2);
                    void trackOnboardingEvent('step_completed', { step: 1 });
                    void navigate({ to: '/onboarding/connect', search: {} as never });
                },
                onError: (error) => {
                    setIsSubmitting(false);
                    toast.error(
                        resolveApiErrorMessage(error, t, {
                            fallbackKey: 'apiErrors.validation.invalidRequestBody',
                            statusFallbacks: apiErrorStatusFallbackPresets.onboardingStateUpdate
                        })
                    );
                }
            }
        );
    }

    const modeSummary = t(`onboardingFlow.personalize.buildModes.${buildMode}.label`).toLowerCase();
    const summary = useMemo(
        () =>
            t('onboardingFlow.personalize.summary', {
                goalCount: goals.length,
                goalLabel:
                    goals.length === 1
                        ? t('onboardingFlow.personalize.goalSingular')
                        : t('onboardingFlow.personalize.goalPlural'),
                mode: modeSummary,
                sources,
                price: formatOnboardingPrice(sources, t)
            }),
        [goals.length, modeSummary, sources, t]
    );

    return (
        <OnboardingShell
            currentStep={1}
            title={t('onboardingFlow.personalize.heroTitle')}
            description=''
            footer={
                <div className='border-t pt-5'>
                    <div className='flex flex-wrap items-center gap-4'>
                        <p className='font-mono text-xs text-muted-foreground'>{summary}</p>
                        <div className='ml-auto'>
                            <Button
                                type='button'
                                size='lg'
                                disabled={isFormDisabled}
                                onClick={() => {
                                    if (isSubmitting) {
                                        return;
                                    }
                                    setIsSubmitting(true);
                                    void submitPersonalization();
                                }}
                            >
                                {t('onboardingFlow.continue')}
                                <ArrowRight className='h-4 w-4' />
                            </Button>
                        </div>
                    </div>
                </div>
            }
        >
            <div className='space-y-8'>
                <Badge variant='muted' appearance='onboarding'>
                    {t('onboardingFlow.personalize.badge')}
                </Badge>

                <section className='space-y-4'>
                    <SectionLabel>{t('onboardingFlow.personalize.goalsLabel')}</SectionLabel>
                    <div className='grid gap-3 sm:grid-cols-2'>
                        {GOALS.map((goal) => {
                            const selected = goals.includes(goal);

                            return (
                                <Toggle
                                    key={goal}
                                    pressed={selected}
                                    disabled={isFormDisabled}
                                    variant='choice'
                                    className='h-auto w-full cursor-pointer items-start justify-start text-left disabled:cursor-not-allowed'
                                    onPressedChange={(nextSelected) => {
                                        toggleGoal(goal);
                                        void trackOnboardingEvent('goal_toggled', { goal, selected: nextSelected });
                                    }}
                                >
                                    <div className='flex items-start gap-3.5'>
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all',
                                                selected
                                                    ? 'border-foreground bg-background'
                                                    : 'border-transparent bg-muted'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'h-2 w-2 rounded-full transition-all',
                                                    selected ? 'bg-foreground' : 'bg-transparent'
                                                )}
                                            />
                                        </span>
                                        <div className='space-y-1'>
                                            <div className='text-sm font-semibold'>
                                                {t(`onboardingFlow.personalize.goals.${goal}.label`)}
                                            </div>
                                            <p className='text-xs leading-5 text-muted-foreground'>
                                                {t(`onboardingFlow.personalize.goals.${goal}.description`)}
                                            </p>
                                        </div>
                                    </div>
                                </Toggle>
                            );
                        })}
                    </div>
                </section>

                <section className='space-y-4'>
                    <SectionLabel>{t('onboardingFlow.personalize.buildModeLabel')}</SectionLabel>
                    <Tabs
                        value={buildMode}
                        onValueChange={(value) => {
                            setBuildMode(value as OnboardingBuildMode);
                            void trackOnboardingEvent('build_mode_set', { mode: value });
                        }}
                    >
                        <TabsList variant='segmented' className='grid h-auto w-full grid-cols-3'>
                            {BUILD_MODES.map((mode) => (
                                <TabsTrigger
                                    key={mode}
                                    value={mode}
                                    disabled={isFormDisabled}
                                    variant='segmented'
                                    size='large'
                                    className='min-h-11'
                                >
                                    {t(`onboardingFlow.personalize.buildModes.${mode}.label`)}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </section>

                <section className='space-y-4'>
                    <SectionLabel>{t('onboardingFlow.personalize.sourcesLabel')}</SectionLabel>
                    <div className='flex flex-wrap items-center gap-4 rounded-xl border p-4'>
                        <div className='flex items-center gap-3'>
                            <Button
                                type='button'
                                variant='outline'
                                size='icon'
                                disabled={isFormDisabled || sources <= MIN_SOURCES}
                                onClick={() => {
                                    const nextSources = Math.max(MIN_SOURCES, sources - 1);
                                    setSources(nextSources);
                                    queueSourcesPatch(nextSources);
                                    void trackOnboardingEvent('sources_set', {
                                        n: nextSources,
                                        est_price: getOnboardingPrice(nextSources)
                                    });
                                }}
                                aria-label={t('onboardingFlow.personalize.decreaseSources')}
                            >
                                <Minus className='h-4 w-4' />
                            </Button>
                            <div className='w-8 text-center text-xl font-semibold'>{sources}</div>
                            <Button
                                type='button'
                                variant='outline'
                                size='icon'
                                disabled={isFormDisabled || sources >= MAX_SOURCES}
                                onClick={() => {
                                    const nextSources = Math.min(MAX_SOURCES, sources + 1);
                                    setSources(nextSources);
                                    queueSourcesPatch(nextSources);
                                    void trackOnboardingEvent('sources_set', {
                                        n: nextSources,
                                        est_price: getOnboardingPrice(nextSources)
                                    });
                                }}
                                aria-label={t('onboardingFlow.personalize.increaseSources')}
                            >
                                <Plus className='h-4 w-4' />
                            </Button>
                        </div>
                        <p className='text-sm text-muted-foreground'>{t('onboardingFlow.personalize.sourcesHelper')}</p>
                    </div>
                </section>
            </div>
        </OnboardingShell>
    );
}

function SectionLabel({ children }: { children: string }) {
    return <div className='font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground'>{children}</div>;
}
